import { useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldAlert, Lock, Cpu, Database, Eye, CreditCard, Users, Cloud, Terminal, CheckCircle2, AlertTriangle, FileCheck } from "lucide-react";

const Security = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            title: "1. Our Commitment to Security",
            content: [
                "At Parakh (operated by Kuberya AI), security is fundamental to everything we do. We understand that you trust us with your personal information and answer sheets, and we take that responsibility seriously. This Security Policy outlines our comprehensive approach to protecting your data."
            ]
        },
        {
            title: "2. Data Encryption",
            icon: <Lock className="w-5 h-5 text-indigo-500" />,
            subsections: [
                {
                    subtitle: "2.1 Data in Transit",
                    points: [
                        "All data transmitted uses TLS 1.3 encryption.",
                        "Secure HTTPS protocol for all communications.",
                        "Encrypted API endpoints for data exchange.",
                        "Protected payment gateway connections."
                    ]
                },
                {
                    subtitle: "2.2 Data at Rest",
                    points: [
                        "AES-256 encryption for stored content.",
                        "Database encryption for personal information.",
                        "Encrypted backups with secure key management.",
                        "Secure file storage systems."
                    ]
                }
            ]
        },
        {
            title: "3. Infrastructure Security",
            icon: <Cloud className="w-5 h-5 text-indigo-500" />,
            subsections: [
                {
                    subtitle: "3.1 Cloud Security",
                    points: [
                        "ISO 27001 certified cloud infrastructure.",
                        "Redundancy and failover protection.",
                        "DDoS protection and traffic filtering.",
                        "24/7 Automated security monitoring."
                    ]
                },
                {
                    subtitle: "3.2 Network Security",
                    points: [
                        "Firewall protection on all layers.",
                        "Intrusion detection (IDS/IPS).",
                        "Network segmentation.",
                        "Regular vulnerability scanning."
                    ]
                }
            ]
        },
        {
            title: "4. Application Security",
            icon: <Terminal className="w-5 h-5 text-indigo-500" />,
            subsections: [
                {
                    subtitle: "4.1 Secure Development",
                    points: [
                        "Security-first development practices.",
                        "Code reviews and dependency scanning.",
                        "Secure API design.",
                        "Protection against OWASP Top 10 vulnerabilities."
                    ]
                },
                {
                    subtitle: "4.2 Authentication",
                    points: [
                        "Strong password requirements.",
                        "Secure hashing with bcrypt.",
                        "Session management with tokens.",
                        "Account lockout mechanisms."
                    ]
                }
            ]
        },
        {
            title: "5. AI Model Security",
            icon: <Cpu className="w-5 h-5 text-indigo-500" />,
            content: [
                "Proprietary AI models protected from unauthorized access.",
                "Rate limiting to prevent model extraction.",
                "Input validation against adversarial attacks.",
                "Anonymized training data handling."
            ]
        },
        {
            title: "6. Data Privacy Controls",
            icon: <Users className="w-5 h-5 text-indigo-500" />,
            content: [
                "Role-Based Access Control (RBAC) for staff.",
                "Principle of least privilege.",
                "Multi-factor authentication for admins.",
                "Logical separation of user data (multi-tenant architecture)."
            ]
        },
        {
            title: "7. Payment Security",
            icon: <CreditCard className="w-5 h-5 text-indigo-500" />,
            content: [
                "PCI DSS compliant processing.",
                "No storage of complete card numbers.",
                "Tokenized payment information.",
                "Encrypted transaction data."
            ]
        },
        {
            title: "8. File Upload Security",
            icon: <FileCheck className="w-5 h-5 text-indigo-500" />,
            content: [
                "Strict file type verification (PDF, IMG only).",
                "Size limits (max 22MB).",
                "Malware and virus scanning.",
                "Sandboxed processing environment.",
                "Secure deletion protocols."
            ]
        },
        {
            title: "9. Monitoring & Response",
            icon: <Eye className="w-5 h-5 text-indigo-500" />,
            content: [
                "24/7 security monitoring and alerting.",
                "Real-time threat detection.",
                "Dedicated incident response team.",
                "Rapid containment protocols."
            ]
        },
        {
            title: "10. Employee Security",
            content: [
                "Mandatory security training and phishing simulations.",
                "Background checks for staff with data access.",
                "Strict confidentiality agreements."
            ]
        },
        {
            title: "11. Third-Party Security",
            content: [
                "Security assessments of all vendors.",
                "Data processing agreements.",
                "Only reputable partners (ISO/PCI certified)."
            ]
        },
        {
            title: "12. Backup & Recovery",
            icon: <Database className="w-5 h-5 text-indigo-500" />,
            content: [
                "Automated daily encrypted backups.",
                "Geographic redundancy.",
                "Comprehensive disaster recovery plan.",
                "Regular drills and testing."
            ]
        },
        {
            title: "13. Compliance",
            content: [
                "Compliant with Information Technology Act, 2000 (India).",
                "Adherence to Indian data protection regulations.",
                "ISO 27001 standards alignment."
            ]
        },
        {
            title: "14. Your Responsibilities",
            content: [
                "Protect your account with a strong password.",
                "Report suspicious activity immediately.",
                "Ensure your device is free from malware.",
                "Verify official website URLs."
            ]
        },
        {
            title: "15. Reporting Security Issues",
            content: [
                "Responsible Disclosure: If you find a vulnerability, report it to kuberyaai@gmail.com.",
                "We promise to acknowledge within 48 hours and credit researchers where appropriate."
            ]
        },
        {
            title: "16. Security Updates",
            content: [
                "Last review: December 2025. Next review: June 2026.",
                "Significant changes will be communicated via email/notification."
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 py-20 px-4 md:px-8">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="bg-white rounded-[2.5rem] shadow-xl p-8 md:p-12"
                >
                    {/* Header */}
                    <div className="text-center mb-16 space-y-4">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 items-center justify-center rounded-full text-indigo-700 font-bold text-sm uppercase tracking-wider mb-4">
                            <ShieldAlert className="w-4 h-4" />
                            Security Protocol
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight">
                            Security <span className="text-indigo-600">Policy</span>
                        </h1>
                      
                    </div>

                    {/* Content */}
                    <div className="space-y-12">
                        {sections.map((section, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                            >
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                                    <span className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-bold">
                                        {idx + 1}
                                    </span>
                                    {section.title.split('. ')[1]}
                                    {section.icon && <span className="ml-auto opacity-20">{section.icon}</span>}
                                </h2>

                                <div className=" space-y-6">
                                    {/* Standard Content Paragraphs */}
                                    {section.content?.map((paragraph, i) => (
                                        <div key={i} className="flex gap-3 text-gray-600 leading-relaxed">
                                            {paragraph.startsWith('•') && <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2.5 flex-shrink-0" />}
                                            <p>{paragraph.replace(/^[•\-\*]\s*/, '')}</p>
                                        </div>
                                    ))}

                                    {/* Subsections Grid */}
                                    {section.subsections && (
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {section.subsections.map((sub, i) => (
                                                <div key={i} className="bg-gray-50 rounded-2xl p-5 border border-gray-100 hover:border-indigo-100 transition-colors">
                                                    <h3 className="font-bold text-gray-900 mb-3 text-base flex items-center gap-2">
                                                        {sub.subtitle}
                                                    </h3>
                                                    <ul className="space-y-2.5">
                                                        {sub.points.map((point, k) => (
                                                            <li key={k} className="flex items-start gap-2.5 text-gray-600 text-sm">
                                                                <CheckCircle2 className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                                                                <span className="leading-snug">{point}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        ))}

                        {/* Assessment Note */}
                        <div className="flex items-start gap-4 p-5 bg-amber-50 border border-amber-100 rounded-2xl mt-8">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="space-y-1">
                                <p className="font-bold text-amber-900 text-sm">Security Advisory</p>
                                <p className="text-sm text-amber-800/80 leading-relaxed">
                                    While we implement robust security measures, no digital system is impenetrable. Continuous vigilance from both our team and yours is essential for maintaining a secure environment.
                                </p>
                            </div>
                        </div>

                        {/* Contact Section */}
                        <div className="mt-16 p-8 bg-indigo-50 rounded-3xl border border-indigo-100">
                            <h2 className="text-xl font-bold text-indigo-900 mb-6 flex items-center gap-2">
                                <ShieldAlert className="w-5 h-5" />
                                17. Contact Our Security Team
                            </h2>
                            <p className="text-indigo-700/80 mb-6">For security-related questions or to report vulnerabilities:</p>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Security Email</span>
                                    <a href="mailto:kuberyaai@gmail.com" className="block text-indigo-900 font-bold hover:text-indigo-600 transition-colors">
                                        kuberyaai@gmail.com
                                    </a>
                                </div>
                                <div className="space-y-2">
                                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Phone</span>
                                    <a href="tel:+919009022058" className="block text-indigo-900 font-bold hover:text-indigo-600 transition-colors">
                                        +91 9009022058
                                    </a>
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Office Address</span>
                                    <p className="text-indigo-900 font-medium leading-relaxed">
                                        Kuberya-AI Office, Vijay Nagar, SBI Chowk,<br />Jabalpur, Madhya Pradesh, India
                                    </p>
                                </div>
                            </div>
                        </div>

                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Security;
